import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

// COMPONENTS
import CharacterCreation from './CharacterCreation/CharacterCreation';
import LanguageToggle from './Common/LanguageToggle';

// CONSTANTS
import { USERS } from '../Helpers/usersData';

// STORE
import { userActions, loginUser, updateUserCharacter } from '../Store/Actions/userActions';
import { getUser } from "../Store/Selectors/selectors";
import { log } from "../Helpers/logger";

const DEV_AUTO_LOGIN_NICKNAME = 'warrior_user';
const DEV_NO_AUTO_LOGIN_FLAG = 'okoyl_dev_no_auto_login';

const AUTHENTICATION_MODE = {
    LOGIN: 'login',
    CHARACTER_CREATION: 'character_creation',
    GAME: 'game'
};

class Authentication extends React.Component {
    constructor(props) {
        super(props);
        
        // Bind methods
        this.handleLogin = this.handleLogin.bind(this);
        this.handleCharacterCreated = this.handleCharacterCreated.bind(this);
        this.handleBackToLogin = this.handleBackToLogin.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);

        this.state = {
            errorMessage: null,
            mode: AUTHENTICATION_MODE.LOGIN,
            user: {
                nickname: '',
                password: ''
            },
            isLoading: false,
            currentUser: null
        };
    }

    handleInputChange(event) {
        const { name, value } = event.target;
        this.setState(prevState => ({
            user: {
                ...prevState.user,
                [name]: value
            },
            errorMessage: null
        }));
    }

    handleLogin(event) {
        event.preventDefault();
        const { nickname, password } = this.state.user;
        
        // Validation
        if (!nickname || !password) {
            this.setState({ errorMessage: 'Please enter both username and password' });
            return;
        }

        this.setState({ isLoading: true, errorMessage: null });

        // Simulate API call with error handling
        try {
            setTimeout(() => {
                try {
                    // Find user in our test accounts
                    const user = USERS.find(u => 
                        u.nickname.toLowerCase() === nickname.toLowerCase() && 
                        u.password === password
                    );
                    
                    if (!user) {
                        throw new Error('Invalid username or password');
                    }
                    
                    log.auth.log('User found:', user);
                    
                    // Update Redux store with user data
                    loginUser(this.props.dispatch, user);
                    
                    // Check if user has a character (hero data)
                    if (user.gameData && user.gameData.hero) {
                        log.auth.log('User has a hero, redirecting to game');
                        this.setState({ 
                            mode: AUTHENTICATION_MODE.GAME,
                            currentUser: user,
                            isLoading: false 
                        }, () => {
                            this.props.navigate('/game');
                        });
                    } else {
                        log.auth.log('No hero found, showing character creation');
                        this.setState({ 
                            mode: AUTHENTICATION_MODE.CHARACTER_CREATION,
                            currentUser: user,
                            isLoading: false,
                            // Pass the nickname to pre-fill in character creation
                            user: {
                                ...this.state.user,
                                nickname: user.nickname // Use the exact case from the user object
                            }
                        });
                    }
                } catch (error) {
                    console.error('Login error:', error);
                    const { t } = this.props;
                    this.setState({
                        errorMessage: error.message || t('login.errorInvalid'),
                        isLoading: false
                    });
                }
            }, 500);
        } catch (error) {
            console.error('Unexpected error during login:', error);
            const { t } = this.props;
            this.setState({
                errorMessage: t('login.errorUnknown'),
                isLoading: false
            });
        }
    }

    handleCharacterCreated(characterData) {
        try {
            const { currentUser } = this.state;
            
            if (!currentUser) {
                throw new Error('No user is currently logged in');
            }
            
            log.auth.log('Character created:', characterData);
            
            // Create the hero object with all required properties
            const hero = {
                ...characterData,
                // Ensure required character properties
                health: characterData.health || 100,
                maxHealth: characterData.maxHealth || 100,
                mana: characterData.mana || 50,
                maxMana: characterData.maxMana || 50,
                level: characterData.level || 1,
                experience: characterData.experience || 0,
                gold: characterData.gold || 100,
                // Add default position if not provided
                position: characterData.position || { mapId: 'map1', x: 0, y: 0 },
                // Add default inventory if not provided
                inventory: characterData.inventory || []
            };
            
            // Update user with new hero data
            const updatedUser = {
                ...currentUser,
                gameData: {
                    ...currentUser.gameData,
                    hero: hero
                },
                hasCharacter: true
            };
            
            log.auth.log('Updating user with character:', updatedUser);
            
            // Save character to Redux store
            updateUserCharacter(this.props.dispatch, updatedUser, currentUser);
            
            // Update local state and redirect to game
            this.setState({ 
                mode: AUTHENTICATION_MODE.GAME,
                currentUser: updatedUser,
                isLoading: false
            }, () => {
                log.auth.log('Redirecting to game after character creation...');
                this.props.navigate('/game');
            });
            
        } catch (error) {
            console.error('Error in handleCharacterCreated:', error);
            const { t } = this.props;
            this.setState({
                errorMessage: t('characterCreation.errors.creation') + ' (' + error.message + ')',
                isLoading: false
            });
        }
    }

    handleBackToLogin() {
        this.setState({ 
            mode: AUTHENTICATION_MODE.LOGIN,
            errorMessage: null,
            user: {
                nickname: this.state.user.nickname,
                password: ''
            }
        });
    }

    render() {
        const { mode, user, errorMessage, isLoading } = this.state;
        const { t } = this.props;

        // Render login form — Okoyl design system
        if (mode === AUTHENTICATION_MODE.LOGIN) {
            return (
                <div className="okframe okframe-vault" data-screen-label="01 Login" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: '100vh', padding: '48px 24px' }}>
                    {/* Language switcher pinned top-right, available before login */}
                    <div style={{ position: 'absolute', top: 20, right: 24, zIndex: 2 }}>
                        <LanguageToggle showLabel={false} />
                    </div>
                    <div className="okfade-up" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img
                            src="/assets/okoyl-logo.png"
                            alt=""
                            style={{ width: 132, height: 132, marginBottom: 24, filter: 'drop-shadow(0 0 36px rgba(200,144,42,0.4))' }}
                        />
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: 56, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--okoyl-200)', fontWeight: 600 }}>
                            Okoyl
                        </div>
                        <div style={{ width: 200, height: 1, background: 'linear-gradient(90deg, transparent, var(--okoyl-400), transparent)', margin: '14px auto 18px' }} />
                        <div className="oklore" style={{ maxWidth: 460, marginBottom: 40, whiteSpace: 'pre-line' }}>
                            {t('login.loreQuote')}
                        </div>

                        {errorMessage && (
                            <div style={{ width: 360, maxWidth: '100%', marginBottom: 18, padding: 12, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--crit)', background: 'rgba(192,57,43,0.10)', border: '1px solid rgba(192,57,43,0.45)', borderRadius: 'var(--r-md)' }}>
                                {errorMessage}
                            </div>
                        )}

                        <form onSubmit={this.handleLogin} style={{ width: 360, maxWidth: '100%' }}>
                            <div style={{ marginBottom: 14, textAlign: 'left' }}>
                                <span className="oklbl oklbl-muted">{t('login.nicknameLabel')}</span>
                                <input
                                    className="okinput"
                                    type="text"
                                    name="nickname"
                                    value={user.nickname}
                                    onChange={this.handleInputChange}
                                    placeholder="Aenar"
                                    required
                                    style={{ marginTop: 6 }}
                                />
                            </div>
                            <div style={{ marginBottom: 24, textAlign: 'left' }}>
                                <span className="oklbl oklbl-muted">{t('login.passwordLabel')}</span>
                                <input
                                    className="okinput"
                                    type="password"
                                    name="password"
                                    value={user.password}
                                    onChange={this.handleInputChange}
                                    placeholder="••••••••••"
                                    required
                                    style={{ marginTop: 6 }}
                                />
                            </div>
                            <button type="submit" disabled={isLoading} className="okbtn okbtn-primary okbtn-full">
                                {isLoading ? t('login.submitting') : t('login.submit')}
                            </button>
                            <div style={{ marginTop: 14 }}>
                                <button type="button" className="okbtn okbtn-ghost okbtn-sm" onClick={() => alert(t('login.createAccountInfo'))}>
                                    {t('login.createAccount')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            );
        }

        // Render character creation
        if (mode === AUTHENTICATION_MODE.CHARACTER_CREATION) {
            return (
                <div className="okframe okframe-page" data-screen-label="02 Character Creation" style={{ minHeight: '100vh' }}>
                    <button
                        onClick={this.handleBackToLogin}
                        className="okbtn okbtn-ghost okbtn-sm"
                        style={{ marginBottom: 16 }}
                    >
                        ← {t('characterCreation.back')}
                    </button>
                    <div style={{ maxWidth: 1024, margin: '0 auto' }}>
                        <CharacterCreation
                            onCharacterCreated={this.handleCharacterCreated}
                            initialValues={{
                                name: this.state.user.nickname || '',
                                classType: 'warrior',
                                gender: 'male',
                                stats: {
                                    strength: 10,
                                    intelligence: 10,
                                    dexterity: 10,
                                    vitality: 10,
                                },
                            }}
                        />
                    </div>
                </div>
            );
        }

        if (mode === AUTHENTICATION_MODE.GAME) {
            return null;
        }
    }
}

const mapStateToProps = (state) => ({
    userInfo: getUser(state)
});

// Pass dispatch through props so the class component can call the
// standalone async helpers (loginUser, updateUserCharacter) directly.
const mapDispatchToProps = (dispatch) => ({ dispatch });

// Create a wrapper component to use the useNavigate hook
const AuthenticationWithRouter = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Dev convenience: auto-login warrior_user once per session unless the user logged out manually.
    React.useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return;
        if (sessionStorage.getItem(DEV_NO_AUTO_LOGIN_FLAG)) return;
        if (props.userInfo?.isAuthenticated) return;

        const warrior = USERS.find(u => u.nickname === DEV_AUTO_LOGIN_NICKNAME);
        if (!warrior) return;

        // Await loginUser so Redux state has settled before navigating —
        // otherwise AppRoutes sees isAuthenticated=false and Core mounts with hero=null.
        loginUser(dispatch, warrior).then(() => {
            navigate('/game');
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return <Authentication {...props} navigate={navigate} />;
};

// Connect component to Redux + i18n
export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(AuthenticationWithRouter));